import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, User, ShoppingCart, LogOut, Sun, Moon, Globe, Heart } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useLanguage } from "@/context/LanguageContext";
import { useWishlist } from "@/context/WishlistContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useProducts } from "@/hooks/useProducts";
import logoLight from "@/assets/logo-light.png";
import logoDark from "@/assets/logo-dark.png";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { cartCount, toggleCart, clearCart } = useCart();
  const { isAuthenticated, user, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { language, toggleLanguage, t } = useLanguage();
  const { wishlistIds, wishlistCount } = useWishlist();
  const { data: allProducts = [] } = useProducts();
  const wishlistProducts = allProducts.filter((p) => wishlistIds.includes(p.id)).slice(0, 4);

  const handleSignOut = async () => {
    clearCart();
    await signOut();
    navigate("/");
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: t.nav.home, path: "/" },
    { name: t.nav.collection, path: "/collection" },
    { name: t.nav.ourStory, path: "/story" },
    { name: t.nav.contact, path: "/contact" },
    { name: t.nav.trackOrder, path: "/track-order" },
  ];

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? "bg-background/95 backdrop-blur-lg border-b border-border shadow-elegant"
            : "bg-gradient-to-b from-black/40 to-transparent dark:from-background/40 dark:to-transparent [&_:where(.nav-icon)]:text-white/80 [&_:where(.nav-icon)]:dark:text-muted-foreground"
        }`}
      >
        <div className="container-luxury">
          <nav className="flex items-center justify-between h-20 md:h-24">
            {/* Logo */}
            <Link to="/" className="flex items-center group">
              <img
                src={theme === "dark" ? logoDark : logoLight}
                alt="Bait Al-Teeb"
                className="h-16 md:h-20 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-10">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`link-underline text-sm tracking-wider uppercase transition-colors ${
                    location.pathname === link.path
                      ? "text-primary"
                      : `${isScrolled ? "text-foreground/80" : "text-white/90 dark:text-foreground/80"} hover:text-primary`
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-4">
              {/* Language Toggle */}
              <button
                onClick={toggleLanguage}
                className="nav-icon hidden md:flex items-center gap-1.5 p-2 text-muted-foreground hover:text-primary transition-colors"
                title={language === "en" ? "العربية" : "English"}
              >
                <Globe className="w-5 h-5" />
                <span className="text-xs font-medium tracking-wide uppercase">
                  {language === "en" ? "AR" : "EN"}
                </span>
              </button>

              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="nav-icon hidden md:flex p-2 text-muted-foreground hover:text-primary transition-colors"
                title={theme === "dark" ? t.nav.lightMode : t.nav.darkMode}
              >
                {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>

              {/* User Actions */}
              {isAuthenticated ? (
                <div className="hidden md:flex items-center gap-1">
                  <Link
                    to="/profile"
                    className="nav-icon p-2 text-muted-foreground hover:text-primary transition-colors"
                    title={language === "en" ? "My Profile" : "حسابي"}
                  >
                    <User className="w-5 h-5" />
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className="nav-icon p-2 text-muted-foreground hover:text-primary transition-colors"
                    title={t.nav.signOut}
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <Link
                  to="/login"
                  className="nav-icon hidden md:flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                >
                  <User className="w-5 h-5" />
                </Link>
              )}

              {/* Wishlist Popover */}
              <Popover>
                <PopoverTrigger asChild>
                  <button className="nav-icon relative p-2 text-foreground hover:text-primary transition-colors hidden md:flex">
                    <Heart className="w-5 h-5" />
                    {wishlistCount > 0 && (
                      <motion.span
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center font-medium"
                      >
                        {wishlistCount}
                      </motion.span>
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-0" align="end">
                  <div className="p-4 border-b border-border">
                    <h3 className="font-serif text-sm">Wishlist ({wishlistCount})</h3>
                  </div>
                  {wishlistProducts.length > 0 ? (
                    <div className="max-h-64 overflow-y-auto">
                      {wishlistProducts.map((product) => (
                        <Link
                          key={product.id}
                          to={`/product/${product.id}`}
                          className="flex items-center gap-3 p-3 hover:bg-muted/50 transition-colors"
                        >
                          <img
                            src={product.image || "/placeholder.svg"}
                            alt={product.name}
                            className="w-12 h-12 object-cover rounded-sm"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{product.name}</p>
                            <p className="text-xs text-primary">{product.price} EGP</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <div className="p-6 text-center text-sm text-muted-foreground">
                      No favorites yet
                    </div>
                  )}
                  <div className="p-3 border-t border-border">
                    <Link
                      to="/wishlist"
                      className="block text-center text-sm text-primary hover:underline"
                    >
                      View All Favorites
                    </Link>
                  </div>
                </PopoverContent>
              </Popover>

              {/* Cart Button */}
              <button
                onClick={toggleCart}
                className="nav-icon relative p-2 text-foreground hover:text-primary transition-colors"
              >
                <ShoppingCart className="w-6 h-6" />
                {cartCount > 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center font-medium"
                  >
                    {cartCount}
                  </motion.span>
                )}
              </button>

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="nav-icon md:hidden p-2 text-foreground"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </nav>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 bg-background pt-24 overflow-y-auto"
          >
            <nav className="flex flex-col items-center gap-6 p-6 pb-24">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={link.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`text-2xl font-serif tracking-wider ${
                      location.pathname === link.path ? "text-primary" : "text-foreground/80"
                    }`}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
              {/* Language Toggle - Mobile */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <button
                  onClick={toggleLanguage}
                  className="flex items-center gap-2 text-lg text-muted-foreground"
                >
                  <Globe className="w-5 h-5" />
                  {language === "en" ? "العربية" : "English"}
                </button>
              </motion.div>
              {/* Theme Toggle - Mobile */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <button
                  onClick={toggleTheme}
                  className="flex items-center gap-2 text-lg text-muted-foreground"
                >
                  {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                  {theme === "dark" ? t.nav.lightMode : t.nav.darkMode}
                </button>
              </motion.div>
              {/* Wishlist - Mobile */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <Link
                  to="/wishlist"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-2 text-lg text-muted-foreground"
                >
                  <Heart className="w-5 h-5" />
                  {t.wishlist.title}
                  {wishlistCount > 0 && (
                    <span className="w-5 h-5 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center font-medium">
                      {wishlistCount}
                    </span>
                  )}
                </Link>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
              >
                {isAuthenticated ? (
                  <div className="flex flex-col items-center gap-6">
                    <Link
                      to="/profile"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex flex-col items-center gap-3"
                    >
                      <div className="w-16 h-16 rounded-full bg-primary/10 border-2 border-primary flex items-center justify-center">
                        <span className="text-xl font-serif font-bold text-primary uppercase">
                          {user?.user_metadata?.full_name
                            ? user.user_metadata.full_name
                                .split(" ")
                                .map((n: string) => n[0])
                                .join("")
                                .slice(0, 2)
                            : user?.email?.[0]?.toUpperCase() || "U"}
                        </span>
                      </div>
                      <div className="text-center">
                        <p className="text-base font-medium text-foreground">
                          {user?.user_metadata?.full_name || user?.email}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {language === "en" ? "View Profile" : "عرض الملف الشخصي"}
                        </p>
                      </div>
                    </Link>
                    <button
                      onClick={() => {
                        handleSignOut();
                        setIsMobileMenuOpen(false);
                      }}
                      className="flex items-center gap-2 text-lg text-muted-foreground"
                    >
                      <LogOut className="w-5 h-5" />
                      {t.nav.signOut}
                    </button>
                  </div>
                ) : (
                  <Link
                    to="/login"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-lg text-muted-foreground"
                  >
                    {t.nav.myAccount}
                  </Link>
                )}
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
